import {
  Add as AddIcon,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  ImageNotSupportedOutlined,
  DragIndicator,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useState } from 'react';
import {
  EditorChapter,
  EditorGuide,
  EditorPick,
  findPick,
  pickImage,
  pickNeedsCopy,
  Selection,
  selectionKey,
  titleText,
} from './model';
import { resolveSiteUrl } from './urls';

interface OutlineProps {
  draft: EditorGuide;
  selection: Selection;
  onSelect: (s: Selection) => void;
  dirty: { guide: boolean; chapters: Set<string>; picks: Set<string> };
  unaddressable: Set<string>;
  readOnly: boolean;
  onMovePick: (pickId: string, chapterId: string, index: number) => void;
  onMoveChapter: (chapterId: string, index: number) => void;
  onAddPlace: (chapterId: string) => void;
  onAddChapter: () => void;
}

const CHAPTER = 'chapter:';
const PICK = 'pick:';
const DROP_BODY = 'drop:';
const DROP_HEAD = 'drop-head:';

function DirtyDot({ label }: { label: string }) {
  return (
    <Tooltip title={label}>
      <Box
        component="span"
        aria-label={label}
        data-testid="dirty-dot"
        sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }}
      />
    </Tooltip>
  );
}

function PickRow({
  pick,
  selected,
  dirty,
  locked,
  disabled,
  onSelect,
}: {
  pick: EditorPick;
  selected: boolean;
  dirty: boolean;
  locked: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${PICK}${pick.id}`,
    disabled: disabled || locked,
  });
  const image = resolveSiteUrl(pickImage(pick));
  const kicker = pick.kicker || (pick.meta ?? [])[0] || '';
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pl: 0.5,
        pr: 1,
        py: 0.5,
        borderRadius: 1,
        cursor: 'pointer',
        opacity: isDragging ? 0.5 : 1,
        bgcolor: selected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
      onClick={onSelect}
      data-testid={`outline-pick-${pick.id}`}
      role="button"
      aria-current={selected}
    >
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled || locked}
        aria-label={`Drag ${titleText(pick.title) || pick.id} to reorder`}
        sx={{ cursor: 'grab', touchAction: 'none' }}
      >
        <DragIndicator fontSize="small" />
      </IconButton>
      <Avatar variant="rounded" src={image} sx={{ width: 36, height: 36, bgcolor: 'action.hover' }}>
        <ImageNotSupportedOutlined fontSize="small" color="disabled" />
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap fontWeight={selected ? 600 : 400}>
          {titleText(pick.title) || pick.id}
        </Typography>
        {kicker && (
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {kicker}
          </Typography>
        )}
      </Box>
      {pickNeedsCopy(pick) && (
        <Tooltip title="Needs copy before it can publish">
          <ErrorOutline fontSize="small" color="warning" data-testid="needs-copy-icon" />
        </Tooltip>
      )}
      {dirty && <DirtyDot label="Unsaved edits" />}
    </Box>
  );
}

function ChapterGroup({
  chapter,
  expanded,
  onToggle,
  selection,
  onSelect,
  dirty,
  unaddressable,
  readOnly,
  onAddPlace,
}: {
  chapter: EditorChapter;
  expanded: boolean;
  onToggle: () => void;
  selection: Selection;
  onSelect: (s: Selection) => void;
  dirty: OutlineProps['dirty'];
  unaddressable: Set<string>;
  readOnly: boolean;
  onAddPlace: (chapterId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${CHAPTER}${chapter.id}`,
    disabled: readOnly,
  });
  const head = useDroppable({ id: `${DROP_HEAD}${chapter.id}`, disabled: expanded });
  const body = useDroppable({ id: `${DROP_BODY}${chapter.id}`, disabled: !expanded });
  const picks = chapter.picks ?? [];
  const selected = selection.kind === 'chapter' && selection.id === chapter.id;

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ mb: 1, opacity: isDragging ? 0.5 : 1 }}
      data-testid={`outline-chapter-${chapter.id}`}
    >
      <Box
        ref={head.setNodeRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pl: 0.5,
          pr: 0.5,
          py: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          bgcolor: selected ? 'action.selected' : head.isOver ? 'action.hover' : 'transparent',
          '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
        }}
        onClick={() => onSelect({ kind: 'chapter', id: chapter.id })}
      >
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          disabled={readOnly}
          aria-label={`Drag chapter ${titleText(chapter.title) || chapter.id} to reorder`}
          sx={{ cursor: 'grab', touchAction: 'none' }}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {titleText(chapter.title) || chapter.id}
        </Typography>
        {dirty.chapters.has(chapter.id) && <DirtyDot label="Unsaved edits" />}
        <Chip size="small" label={picks.length} aria-label={`${picks.length} places`} />
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-label={expanded ? 'Collapse chapter' : 'Expand chapter'}
        >
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ pl: 1.5 }}>
          <SortableContext
            items={picks.map((p) => `${PICK}${p.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {picks.map((pick) => (
              <PickRow
                key={pick.id}
                pick={pick}
                selected={selection.kind === 'pick' && selection.id === pick.id}
                dirty={dirty.picks.has(pick.id)}
                locked={unaddressable.has(pick.id)}
                disabled={readOnly}
                onSelect={() => onSelect({ kind: 'pick', id: pick.id })}
              />
            ))}
          </SortableContext>
          <Box
            ref={body.setNodeRef}
            sx={{
              minHeight: 28,
              borderRadius: 1,
              border: '1px dashed',
              borderColor: body.isOver ? 'primary.main' : 'transparent',
            }}
          >
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={readOnly}
              onClick={() => onAddPlace(chapter.id)}
            >
              Add place
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function OutlinePanel(props: OutlineProps) {
  const { draft, selection, onSelect, dirty, readOnly } = props;
  const chapters = draft.chapters ?? [];
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // A chapter drag only lands on chapters; a pick drag only on picks and chapter ends.
  const collision: CollisionDetection = (args) => {
    const dragging = String(args.active.id);
    const allowed = dragging.startsWith(CHAPTER)
      ? (id: string) => id.startsWith(CHAPTER)
      : (id: string) => id.startsWith(PICK) || id.startsWith(DROP_BODY) || id.startsWith(DROP_HEAD);
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((c) => allowed(String(c.id))),
    });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const a = String(active.id);
    const o = String(over.id);
    if (a.startsWith(CHAPTER)) {
      const to = chapters.findIndex((c) => c.id === o.slice(CHAPTER.length));
      const from = chapters.findIndex((c) => c.id === a.slice(CHAPTER.length));
      if (to >= 0 && from >= 0 && to !== from) props.onMoveChapter(a.slice(CHAPTER.length), to);
      return;
    }
    if (!a.startsWith(PICK)) return;
    const pickId = a.slice(PICK.length);
    const from = findPick(draft, pickId);
    if (!from) return;
    if (o.startsWith(PICK)) {
      const target = findPick(draft, o.slice(PICK.length));
      // Same chapter: the dragged pick takes the over pick's place (the list's own
      // array-move rule). Another chapter: it goes in front of the over pick.
      if (target) props.onMovePick(pickId, target.chapter.id, target.index);
      return;
    }
    const chapterId = o.startsWith(DROP_BODY) ? o.slice(DROP_BODY.length) : o.slice(DROP_HEAD.length);
    const chapter = chapters.find((c) => c.id === chapterId);
    if (!chapter) return;
    const count = (chapter.picks ?? []).length;
    // `index` is the position after the pick has been taken out: the end of the chapter.
    props.onMovePick(pickId, chapterId, from.chapter.id === chapterId ? count - 1 : count);
  };

  const isExpanded = (chapterId: string) => !collapsed[chapterId];

  // Selecting a pick (from the form, a dialog or an error link) opens its chapter.
  const selectedPickChapter =
    selection.kind === 'pick' ? findPick(draft, selection.id)?.chapter.id : undefined;
  useEffect(() => {
    if (selectedPickChapter) setCollapsed((prev) => ({ ...prev, [selectedPickChapter]: false }));
  }, [selectedPickChapter, selection]);

  return (
    <Box data-testid="outline">
      <Box
        onClick={() => onSelect({ kind: 'guide' })}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          mb: 1,
          borderRadius: 1,
          cursor: 'pointer',
          bgcolor: selection.kind === 'guide' ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: selection.kind === 'guide' ? 'action.selected' : 'action.hover' },
        }}
        data-testid="outline-guide"
        aria-current={selectionKey(selection) === 'guide'}
        role="button"
      >
        <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
          {titleText(draft.title) || 'Guide'}
        </Typography>
        {dirty.guide && <DirtyDot label="Unsaved edits" />}
      </Box>
      <DndContext sensors={sensors} collisionDetection={collision} onDragEnd={onDragEnd}>
        <SortableContext
          items={chapters.map((c) => `${CHAPTER}${c.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {chapters.map((chapter) => (
            <ChapterGroup
              key={chapter.id}
              chapter={chapter}
              expanded={isExpanded(chapter.id)}
              onToggle={() =>
                setCollapsed((prev) => ({ ...prev, [chapter.id]: isExpanded(chapter.id) }))
              }
              selection={selection}
              onSelect={onSelect}
              dirty={dirty}
              unaddressable={props.unaddressable}
              readOnly={readOnly}
              onAddPlace={props.onAddPlace}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button startIcon={<AddIcon />} disabled={readOnly} onClick={props.onAddChapter} sx={{ mt: 1 }}>
        Add chapter
      </Button>
    </Box>
  );
}
